/**
 * <predictive-search>: search-as-you-type inside the search overlay.
 *
 * The input is an ARIA combobox. Results are server-rendered by
 * sections/predictive-search.liquid through Shopify's predictive search API
 * and every result is a listbox option navigable with the arrow keys.
 */
import { debounce, events } from '@theme/core';

class PredictiveSearch extends HTMLElement {
  #cache = new Map();
  #controller = null;
  #activeIndex = -1;

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="search"]');
    this.results = this.querySelector('[data-search-results]');
    this.empty = this.querySelector('[data-search-empty]');
    this.status = this.querySelector('[data-search-status]');
    this.clearButton = this.querySelector('[data-search-clear]');
    if (!this.input || !this.results) return;

    this.input.addEventListener('input', debounce(() => this.#onInput(), 220));
    this.input.addEventListener('keydown', (event) => this.#onKeydown(event));

    this.form.addEventListener('submit', (event) => {
      const option = this.#options()[this.#activeIndex];
      if (option && !option.matches('[data-predictive-submit]')) {
        event.preventDefault();
        option.click();
      }
      if (!this.input.value.trim()) event.preventDefault();
    });

    this.form.addEventListener('reset', () => {
      requestAnimationFrame(() => {
        this.#showEmptyState();
        this.input.focus();
      });
    });

    this.offOpen = events.on('overlay:open', ({ name }) => {
      if (name !== 'search') return;
      this.input.focus();
      this.input.select();
    });

    if (this.input.value.trim()) this.#onInput();
  }

  disconnectedCallback() {
    this.offOpen?.();
    this.#controller?.abort();
  }

  #options() {
    return [...this.results.querySelectorAll('[role="option"]')];
  }

  async #onInput() {
    const terms = this.input.value.trim();
    this.clearButton.hidden = terms.length === 0;

    if (!terms) {
      this.#showEmptyState();
      return;
    }

    try {
      const html = await this.#fetch(terms);
      // Ignore late responses for a query the visitor already changed
      if (terms !== this.input.value.trim()) return;
      this.#render(html);
    } catch (error) {
      if (error.name !== 'AbortError') this.#showEmptyState();
    }
  }

  async #fetch(terms) {
    if (this.#cache.has(terms)) return this.#cache.get(terms);

    this.#controller?.abort();
    this.#controller = new AbortController();
    this.setAttribute('aria-busy', 'true');

    const params = new URLSearchParams({
      q: terms,
      section_id: 'predictive-search',
      'resources[type]': this.dataset.types || 'product,query',
      'resources[limit]': this.dataset.limit || '6',
      'resources[limit_scope]': 'each',
      'resources[options][unavailable_products]': 'last',
    });

    try {
      const response = await fetch(`${this.dataset.url}?${params}`, { signal: this.#controller.signal });
      if (!response.ok) throw new Error(`Predictive search failed: ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const html = doc.querySelector('[data-predictive-content]')?.innerHTML ?? '';
      this.#cache.set(terms, html);
      return html;
    } finally {
      this.removeAttribute('aria-busy');
    }
  }

  #render(html) {
    this.results.innerHTML = html;
    this.results.hidden = false;
    this.empty.hidden = true;
    this.#activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'true');
    this.input.removeAttribute('aria-activedescendant');

    const message = this.results.querySelector('[data-predictive-status]')?.textContent.trim();
    if (message) this.status.textContent = message;
  }

  #showEmptyState() {
    this.#controller?.abort();
    this.results.hidden = true;
    this.results.innerHTML = '';
    this.empty.hidden = false;
    this.clearButton.hidden = true;
    this.#activeIndex = -1;
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.status.textContent = '';
  }

  #onKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.#move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.#move(-1);
        break;
      case 'Escape':
        // First Escape clears the query; the next one closes the overlay
        if (this.input.value) {
          event.preventDefault();
          event.stopPropagation();
          this.form.reset();
        }
        break;
      default:
    }
  }

  #move(step) {
    const options = this.#options();
    if (!options.length) return;

    options[this.#activeIndex]?.setAttribute('aria-selected', 'false');

    // Positions cycle through -1 (the input itself) and every option
    let next = this.#activeIndex + step;
    if (next >= options.length) next = -1;
    if (next < -1) next = options.length - 1;
    this.#activeIndex = next;

    const option = options[next];
    if (!option) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }
    option.setAttribute('aria-selected', 'true');
    option.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', option.id);
  }
}

if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);
